/**
 * Inline version-chain expansion, shared by the documents list and the document
 * picker.
 *
 * A row that heads a version group can be expanded to reveal its superseded
 * revisions. They are fetched on first expansion, cached for the lifetime of the
 * component, and rendered beneath the anchor. Collapsing keeps the cache so a
 * re-expand is instant.
 *
 * Both surfaces must agree on what a version chain is — which revisions are
 * shown, in what order, when the cache goes stale and what happens when the
 * fetch fails — so the logic lives here rather than being copied. `mapRow` lets
 * each caller keep its own row shape.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { fetchDocumentsInVersionGroup } from "@/stores/documents/repository";
import type { DocumentRowMapped } from "@/stores/documents/repository/types";

/** One cached chain: the revisions, plus the anchor they were fetched against. */
export interface VersionCacheEntry<T> {
  /** The row that was excluded from the fetch because it is already rendered.
   *  When the anchor changes the entry is stale — see `useStaleAnchorEviction`. */
  anchorId: string;
  docs: T[];
}

export interface VersionExpansion<T> {
  /** Is this group currently expanded? */
  isExpanded: (versionGroup: string | null | undefined) => boolean;
  /** The expanded groups, for callers that pass the raw set to a pure helper. */
  expandedGroups: ReadonlySet<string>;
  /** Is this group's first fetch in flight? */
  isLoading: (versionGroup: string | null | undefined) => boolean;
  /**
   * The group's older revisions, or `undefined` when not yet fetched. An empty
   * array is meaningful: every earlier revision has been hard-deleted, which
   * callers should say rather than render as nothing.
   */
  getVersions: (versionGroup: string | null | undefined) => T[] | undefined;
  /** Expand (fetching on first open) or collapse. */
  toggle: (versionGroup: string | null, anchorDocId: number | string) => Promise<void>;
  /** The raw cache, for callers that flatten every loaded revision (selection,
   *  bulk actions) rather than reading one chain at a time. */
  cache: Map<string, VersionCacheEntry<T>>;
  /** Drop these groups and collapse them, so the next open refetches. */
  evict: (versionGroups: string[]) => void;
}

export function useVersionExpansion<T>(mapRow: (row: DocumentRowMapped) => T): VersionExpansion<T> {
  const { toast } = useToast();
  const { t } = useLanguage();

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set());
  const [loadingGroups, setLoadingGroups] = useState<Set<string>>(() => new Set());
  const [cache, setCache] = useState<Map<string, VersionCacheEntry<T>>>(() => new Map());

  // What `toggle` decides from. A setState updater's body runs at render time,
  // not at the call, so deciding open-vs-shut inside one reads a value React
  // may not have computed yet: a second click in the same tick would queue
  // collapse and expand back to back and the click would appear to do nothing.
  // Every write goes through `writeExpanded`, so the ref leads the state by at
  // most the current statement.
  const expandedRef = useRef(expandedGroups);
  const writeExpanded = useCallback((next: Set<string>) => {
    expandedRef.current = next;
    setExpandedGroups(next);
  }, []);

  const isExpanded = useCallback(
    (vg: string | null | undefined) => !!vg && expandedGroups.has(vg),
    [expandedGroups]
  );

  const isLoading = useCallback(
    (vg: string | null | undefined) => !!vg && loadingGroups.has(vg),
    [loadingGroups]
  );

  const getVersions = useCallback(
    (vg: string | null | undefined) => (vg ? cache.get(vg)?.docs : undefined),
    [cache]
  );

  const evict = useCallback(
    (versionGroups: string[]) => {
      if (versionGroups.length === 0) return;
      setCache((prev) => {
        const next = new Map(prev);
        versionGroups.forEach((k) => next.delete(k));
        return next;
      });
      if (!versionGroups.some((k) => expandedRef.current.has(k))) return;
      const next = new Set(expandedRef.current);
      versionGroups.forEach((k) => next.delete(k));
      writeExpanded(next);
    },
    [writeExpanded]
  );

  const toggle = useCallback(
    async (versionGroup: string | null, anchorDocId: number | string) => {
      if (!versionGroup) return;

      // Collapse if already open.
      if (expandedRef.current.has(versionGroup)) {
        const next = new Set(expandedRef.current);
        next.delete(versionGroup);
        writeExpanded(next);
        return;
      }

      const expand = () => writeExpanded(new Set(expandedRef.current).add(versionGroup));

      // Already fetched — expand straight away.
      if (cache.has(versionGroup)) {
        expand();
        return;
      }

      setLoadingGroups((prev) => new Set(prev).add(versionGroup));
      try {
        const anchor = typeof anchorDocId === "number" ? anchorDocId : parseInt(anchorDocId, 10);
        const rows = await fetchDocumentsInVersionGroup(
          versionGroup,
          Number.isFinite(anchor) ? anchor : undefined
        );
        setCache((prev) =>
          new Map(prev).set(versionGroup, {
            anchorId: String(anchorDocId),
            docs: rows.map(mapRow),
          })
        );
        expand();
      } catch (err) {
        console.error("[useVersionExpansion] Failed to load version history:", err);
        toast({
          variant: "destructive",
          title: t.documents.versioning.history.fetchFailedTitle,
          description:
            err instanceof Error
              ? err.message
              : t.documents.versioning.history.fetchFailedDescription,
        });
      } finally {
        setLoadingGroups((prev) => {
          const next = new Set(prev);
          next.delete(versionGroup);
          return next;
        });
      }
    },
    [cache, mapRow, toast, t, writeExpanded]
  );

  return { isExpanded, expandedGroups, isLoading, getVersions, toggle, cache, evict };
}

/**
 * Evict cached chains whose anchor row has changed identity — e.g. a new upload
 * flipped v2→v3, so the entry (fetched with v2 excluded) no longer matches what
 * is rendered. Collapsing forces a fresh fetch on the next open.
 */
export function useStaleAnchorEviction<T>(
  expansion: VersionExpansion<T>,
  currentAnchors: Array<{ id: string; version_group: string | null }>
): void {
  const { cache, evict } = expansion;
  useEffect(() => {
    if (cache.size === 0) return;
    const anchorByGroup = new Map<string, string>();
    for (const doc of currentAnchors) {
      if (doc.version_group) anchorByGroup.set(doc.version_group, doc.id);
    }
    const stale: string[] = [];
    for (const [vg, entry] of cache) {
      const current = anchorByGroup.get(vg);
      if (current && entry.anchorId !== current) stale.push(vg);
    }
    evict(stale);
  }, [currentAnchors, cache, evict]);
}
